/**
 * @fileoverview 이상 지출 탐지 유틸리티 함수
 * 월별 지출 통계, 카테고리 평균, 이상치 임계값 계산 등 순수 함수 모음
 */

/**
 * 거래 배열에서 월별 지출 합계를 계산한다.
 * @param {Array} transactions - 거래 배열
 * @returns {Object} { 'YYYY-MM': totalAmount, ... }
 */
export const calculateMonthlyExpenses = (transactions) => {
  const monthly = {};
  transactions
    .filter((t) => t.type === 'expense')
    .forEach((t) => {
      const amount = Number(t.amount);
      if (isNaN(amount) || amount < 0) return;
      const month = t.date.slice(0, 7);
      monthly[month] = (monthly[month] || 0) + amount;
    });
  return monthly;
};

/**
 * 카테고리별 월 지출 이력에서 카테고리 평균을 계산한다.
 * @param {Array} transactions - 거래 배열
 * @returns {Object} { categoryName: averageAmount, ... }
 */
export const calculateCategoryAverages = (transactions) => {
  const history = {};
  transactions
    .filter((t) => t.type === 'expense')
    .forEach((t) => {
      const amount = Number(t.amount);
      if (isNaN(amount) || amount < 0) return;
      const month = t.date.slice(0, 7);
      const category = t.category || '미분류';
      if (!history[category]) history[category] = {};
      history[category][month] = (history[category][month] || 0) + amount;
    });

  const averages = {};
  Object.entries(history).forEach(([cat, months]) => {
    const values = Object.values(months);
    averages[cat] = values.reduce((a, b) => a + b, 0) / values.length;
  });
  return averages;
};

/**
 * 월별 지출 값 배열에서 이상치 임계값을 계산한다.
 * 공식: mean + 1.2 × stdDev
 * @param {number[]} values - 월별 지출 금액 배열 (length >= 3)
 * @returns {{ mean: number, stdDev: number, threshold: number }}
 */
export const calculateAnomalyThreshold = (values) => {
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variance =
    values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const threshold = mean + 1.2 * stdDev;
  return { mean, stdDev, threshold };
};

/**
 * 임계값을 초과하는 월을 식별하고, 금액 내림차순 정렬 후 최대 3건 반환한다.
 * @param {Object} monthlyExpenses - { 'YYYY-MM': amount }
 * @param {number} threshold - 이상치 임계값
 * @returns {Array<[string, number]>} [[month, amount], ...] (최대 3건, 내림차순)
 */
export const identifyAnomalies = (monthlyExpenses, threshold) => {
  return Object.entries(monthlyExpenses)
    .filter(([, amount]) => Number.isFinite(amount) && amount > 0 && amount > threshold)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
};

/**
 * 해당 월의 카테고리별 지출에서 평소 대비 차이가 가장 큰 카테고리를 찾는다.
 * @param {Object} currentMonthCategories - { category: amount }
 * @param {Object} categoryAverages - { category: averageAmount }
 * @returns {{ name: string, diff: number } | null}
 */
export const findMainCauseCategory = (currentMonthCategories, categoryAverages) => {
  let mainCause = null;
  let maxDiff = -Infinity;

  Object.entries(currentMonthCategories).forEach(([cat, amount]) => {
    const usual = categoryAverages[cat] || 0;
    const diff = amount - usual;
    if (diff > maxDiff) {
      maxDiff = diff;
      mainCause = { name: cat, diff };
    }
  });

  return mainCause;
};

/**
 * 거래 배열에서 금액이 가장 큰 단일 거래를 찾는다.
 * @param {Array} transactions - 거래 배열
 * @returns {{ description: string, amount: number } | null}
 */
export const findLargestExpense = (transactions) => {
  if (!transactions || transactions.length === 0) return null;
  const sorted = [...transactions].sort((a, b) => Number(b.amount) - Number(a.amount));
  const largest = sorted[0];
  return {
    description:
      largest.description && largest.description !== '알림에서 추출'
        ? largest.description
        : largest.merchant || largest.category || '정보 없음',
    amount: Number(largest.amount),
  };
};
